// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a long paste folded where it was pasted (PastedText.ts): "Pasted text", its lines and size,
// and its first two lines. The same fold shows in the chat and Agents composers and in sent messages
// and prompts; pressing it opens the text in the shared file preview (AttachmentPreviewModal).

import {AttachmentPreviewModal} from '@app/features/messaging/components/AttachmentPreviewModal';
import {useNearViewport} from '@app/features/messaging/hooks/useNearViewport';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {countLines} from '@app/features/pasted_text/PastedText';
import styles from '@app/features/pasted_text/PastedTextFold.module.css';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconArrowsDiagonal, IconFileText, IconX} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {useEffect, useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Pasted text',
	comment:
		'Long paste folded into a block in a message or prompt: its title. The pasted text is sent as a text file with the message.',
});
const LINES_DESCRIPTOR = msg({
	message: '{count, plural, one {# line} other {# lines}}',
	comment: 'Long paste folded into a block: how many lines the pasted text has, shown before its size.',
});
const OPEN_DESCRIPTOR = msg({
	message: 'Open pasted text',
	comment: 'Long paste folded into a block: accessible label of the block, which opens the whole text.',
});
const REMOVE_DESCRIPTOR = msg({
	message: 'Remove pasted text',
	comment: 'Long paste folded into a block in the message box: takes the paste out of the message.',
});

/** Where a pasted file comes from: a file in the composer, or an attachment fetched when shown. */
export type PastedTextSource = {
	/** Stable per file (an attachment id or URL); a File is its own key. */
	key: string | File;
	name: string;
	size: number | null;
	loadBlob: () => Promise<Blob | null>;
};

type Summary = {lines: number; peek: string} | null;

const PEEK_LINES = 2;
const RETRY_MS = 2000;
const summaries = new Map<string, Summary>();
const fileSummaries = new WeakMap<File, Summary>();

async function summarise(source: PastedTextSource): Promise<Summary> {
	try {
		const blob = source.key instanceof File ? source.key : await source.loadBlob();
		if (!blob) return null;
		const text = await blob.text();
		const peek = text
			.split('\n')
			.filter((line) => line.trim())
			.slice(0, PEEK_LINES)
			.map((line) => line.slice(0, 400))
			.join('\n');
		return {lines: countLines(text), peek};
	} catch {
		return null;
	}
}

/** The same paste by name and size: a sent prompt's file, read already from the copy on this device. */
const sameFile = (source: PastedTextSource) => `${source.name}|${source.size ?? ''}`;

function cached(source: PastedTextSource): Summary | undefined {
	const own = source.key instanceof File ? fileSummaries.get(source.key) : summaries.get(source.key);
	return own ?? summaries.get(sameFile(source)) ?? undefined;
}

/** The lines and first lines of a pasted file, read once per file and kept for later folds. */
function useSummary(source: PastedTextSource | null, waiting: boolean): Summary {
	const latest = useRef(source);
	latest.current = source;
	const key = source?.key ?? null;
	const [summary, setSummary] = useState<Summary>(() => (source ? (cached(source) ?? null) : null));
	useEffect(() => {
		const current = latest.current;
		if (!current || key === null) {
			setSummary(null);
			return;
		}
		const known = cached(current);
		if (known !== undefined) {
			setSummary(known);
			return;
		}
		if (waiting) return;
		let live = true;
		let retry: ReturnType<typeof setTimeout> | undefined;
		const read = (tries: number) =>
			void summarise(current).then((next) => {
				if (!live) return;
				if (!next && tries > 0) {
					// The file may not have reached the machine or the server yet.
					retry = setTimeout(() => read(tries - 1), RETRY_MS);
					return;
				}
				if (key instanceof File) fileSummaries.set(key, next);
				else if (next) summaries.set(key, next);
				if (next) summaries.set(sameFile(current), next);
				setSummary(next);
			});
		read(1);
		return () => {
			live = false;
			clearTimeout(retry);
		};
	}, [key, waiting]);
	return summary;
}

export function openPastedText(source: PastedTextSource): void {
	ModalCommands.push(
		modal(() => (
			<AttachmentPreviewModal
				attachment={{name: source.name, content_type: 'text/plain', size: source.size ?? 0}}
				loadBlob={source.key instanceof File ? async () => source.key as File : source.loadBlob}
				onClose={() => ModalCommands.pop()}
			/>
		)),
	);
}

export function PastedTextFold({
	source,
	onRemove,
	className,
	flx,
}: {
	/** Null while the file isn't known (a draft whose file is gone): the fold still shows, without a peek. */
	source: PastedTextSource | null;
	/** In a composer: the × that takes the paste out. */
	onRemove?: () => void;
	className?: string;
	flx: string;
}) {
	const {i18n} = useLingui();
	const {ref, isNearViewport} = useNearViewport<HTMLDivElement>({disabled: source?.key instanceof File});
	const summary = useSummary(source, !isNearViewport);
	const size = source?.size ?? null;
	const meta = [
		summary ? i18n._(LINES_DESCRIPTOR, {count: summary.lines}) : null,
		size !== null ? formatFileSize(size) : null,
	]
		.filter(Boolean)
		.join(' · ');
	return (
		<div ref={ref} className={clsx(styles.fold, className)} data-flx={flx} contentEditable={false}>
			<button
				type="button"
				className={styles.open}
				onClick={() => source && openPastedText(source)}
				disabled={!source}
				aria-label={i18n._(OPEN_DESCRIPTOR)}
				data-flx={`${flx}.open`}
			>
				<span className={styles.head}>
					<IconFileText size={15} stroke={1.7} className={styles.icon} aria-hidden="true" />
					<span className={styles.title}>{i18n._(TITLE_DESCRIPTOR)}</span>
					{meta && <span className={styles.meta}>{meta}</span>}
					<IconArrowsDiagonal size={14} stroke={1.8} className={styles.expand} aria-hidden="true" />
				</span>
				{summary?.peek && <span className={styles.peek}>{summary.peek}</span>}
			</button>
			{onRemove && (
				<button
					type="button"
					className={styles.remove}
					onClick={onRemove}
					aria-label={i18n._(REMOVE_DESCRIPTOR)}
					title={i18n._(REMOVE_DESCRIPTOR)}
					data-flx={`${flx}.remove`}
				>
					<IconX size={12} stroke={2.2} />
				</button>
			)}
		</div>
	);
}
